import { useEffect, useState, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Layout, Space, Table, Tooltip, Form, Select, Input, Row, Col, DatePicker, Typography, message, Alert, TreeSelect, Modal, Popover, Radio, Checkbox, Dropdown } from 'antd';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { dispatchTableScrollYRemeasure, useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import conferenceSlice, { conferenceActions } from '../store/conferenceSlice';
import { conferenceOnceApi } from '@services/Conference';
import { CONFERENCE_WORKSPACE_PARENT_PATH, CONFERENCE_WORKSPACE_ROUTE_BASE } from '@/layouts/conferenceWorkspaceConfig';
import {
    guestjourneysApi,
    batchUpdateNeedsApi,
    GuestJourneyTotalApi,
    savePartnersApi,
    updatePartnerDeadlineApi,
    saveConferencePartnersApi,
    deleteConferencePartnersApi,
    updateConferencePartnersApi,
    getPartnersApi,
    listWithConferenceInfoApi,
    getHotelListDayApi,
    saveGuestJourney,
    updateGuestJourneyIdCard,
    updateGuestJourneyRemindApi,
} from '@services/Guest';
import PageHeader from '@components/PageHeader/PageHeader';
import { getConferenceListApi } from '@services/Agenda';
import './GuestJourneyList.less';
import { expertLevels, cardTypes } from '@utils/constants';
import { filterFormItemLayout, filterFormGridLayout, fullPagerConfig } from '@utils/property';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';
import { localStorageGet } from '@core/cookie';
import GuestJourneySetting from './components/GuestJourneySetting';
import GuestJourneyImport from './components/GuestJourneyImport';
import config from '@config';
import { filterTreeNodeByTitle } from '@/utils/antdTreeSelect';
import HotelManagementDrawer from './components/HotelManagementDrawer';
import JourneyInfoDisplay from './components/JourneyInfoDisplay';
import HotelInfoDisplay from './components/HotelInfoDisplay';
import JourneyEditModal from './components/JourneyEditModal';
import TransportChangeHistoryModal from './components/TransportChangeHistoryModal';
import HotelEditModal from './components/HotelEditModal';
import CarEditModal from './components/CarEditModal';
import IdCardEditModal from '@/components/common/IdCardEditModal';
import GuestTaskList from '@/components/common/GuestTaskList';
import WorkExperiences from '@/components/common/WorkExperiences';
import { logIssueTicket, logCancelTicket } from '@/utils/conferenceLogger';
import { getOperationLogListWithExtra } from '@/services/Applog/ApplogService';
import GuestJourneyFilterPanel from './GuestJourneyFilterPanel';
const { Text } = Typography;

const { setGuestJourneyFilter: setFilter, setGuestJourneyCollapsed } = conferenceActions;

// 特别提醒引导文案（列标题 Tooltip 与编辑弹窗共用）
const REMIND_PREVIEW_TIP = (
    <>
        特别提醒，将展示在
        <span className="tooltip-highlight">专家行程清单</span>
        中。如需预览实际效果，可点击专家对应“
        <span className="tooltip-highlight">预览行程单</span>
        ”查看。
    </>
);

/**
 * 获取系统建议时间（来程/返程通用）
 * @param {Array} agendasGuestsList - 嘉宾任务列表
 * @param {string} type - 类型：'arrival' 来程 | 'return' 返程
 * @param {string} format - 时间格式，默认 'M月D日 HH:mm'
 * @returns {string|undefined} 格式化后的时间字符串
 */
const getSuggestedTime = (agendasGuestsList, type, format = 'M月D日 HH:mm') => {
    if (!agendasGuestsList || agendasGuestsList.length === 0) {
        return undefined;
    }

    if (type === 'arrival') {
        // 来程：第一个已确认任务的开始时间
        for (const task of agendasGuestsList) {
            if (task.invitationAccepted === 1) {
                return dayjs(task.startTime).format(format);
            }
        }
    } else {
        // 返程：最后一个已确认任务的结束时间
        for (let i = agendasGuestsList.length - 1; i >= 0; i--) {
            const task = agendasGuestsList[i];
            if (task.invitationAccepted === 1) {
                return dayjs(task.endTime).format(format);
            }
        }
    }
    return undefined;
};

const GuestJourneyList = () => {
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const conferenceId = Number(params?.id) || 0; // 会议项目 ID
    const [conference, setConference] = useState(null);

    const [form] = Form.useForm();
    const [authServiceForm] = Form.useForm();
    const [editAuthForm] = Form.useForm();

    const dispatch = useDispatch();
    // 嘉宾行程筛选条件
    const filter = useSelector(state => state.conference.guestJourney.filter);

    // 嘉宾行程列表
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);

    // 筛选表单项
    const [dateOptions, setDateOptions] = useState([]); //任务日期

    const [selectedExpertsIds, setSelectedExpertsIds] = useState([]);

    // 批量更换服务商（授权服务商） Modal
    const [vendorForm] = Form.useForm();
    const [visibleBulkChangeVendor, setVisibleBulkChangeVendor] = useState(false);
    const [vendorOptions, setVendorOptions] = useState([]);
    const [transportVendorOptions, setTransportVendorOptions] = useState([]);
    const [carVendorOptions, setCarVendorOptions] = useState([]);
    const [hotelVendorOptions, setHotelVendorOptions] = useState([]);
    const [selectedExperts, setSelectedExperts] = useState([]); // 保存选中的专家信息

    // 确认行程需求
    const [journeyForm] = Form.useForm();
    const [visibleConfirmJourney, setVisibleConfirmJourney] = useState(false);

    // 行程单设置
    const [visibleGuestJourneySetDrawer, setVisibleGuestJourneySetDrawer] = useState(false);

    // 导入行程单
    const [importDataOpen, setImportDataOpen] = useState(false);

    // 合计信息
    const [guestTotalInfo, setGuestTotalInfo] = useState();

    // 会议列表
    const [conferenceHallTreeData, setConferenceHallTreeData] = useState([]);

    // 酒店列表选项
    const [hotelOptions, setHotelOptions] = useState([]);
    const [hotelRoomTypeOptions, setHotelRoomTypeOptions] = useState([]);
    const [selectedHotelId, setSelectedHotelId] = useState(null);

    const collapsed = useSelector(state => state.conference.guestJourney.collapsed ?? true);
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();
    const collapsedRemeasureMountedRef = useRef(false);

    useLayoutEffect(() => {
        if (!collapsedRemeasureMountedRef.current) {
            collapsedRemeasureMountedRef.current = true;
            return;
        }
        dispatchTableScrollYRemeasure();
    }, [collapsed]);

    // 授权服务商
    const [authServiceProviderOpen, setAuthServiceProviderOpen] = useState(false);
    const [authDeadline, setAuthDeadline] = useState(null); // 添加授权截止日期状态
    const [isEditingDeadline, setIsEditingDeadline] = useState(false); // 添加编辑状态
    const [authTableData, setAuthTableData] = useState([]);
    const [addAuthServiceOpen, setAddAuthServiceOpen] = useState(false);
    const [addAuthServiceForm] = Form.useForm();
    const [editingAuth, setEditingAuth] = useState(null); // 正在编辑的授权信息
    const [vendorSearchValue, setVendorSearchValue] = useState(''); // 服务商搜索值

    const [hotelDrawerVisible, setHotelDrawerVisible] = useState(false);

    // 变更行程交通弹窗
    const [isJourneyEditModalVisible, setIsJourneyEditModalVisible] = useState(false);
    const [editingJourneyInfo, setEditingJourneyInfo] = useState(null);

    // 变更住宿信息弹窗
    const [isHotelEditModalVisible, setIsHotelEditModalVisible] = useState(false);
    const [editingHotelInfo, setEditingHotelInfo] = useState(null);

    // 变更用车信息弹窗
    const [isCarEditModalVisible, setIsCarEditModalVisible] = useState(false);
    const [editingCarInfo, setEditingCarInfo] = useState(null);

    // 交通变更记录弹窗
    const [isTransportHistoryModalVisible, setIsTransportHistoryModalVisible] = useState(false);
    const [viewingTransportHistory, setViewingTransportHistory] = useState(null);
    const [transportChangeRecords, setTransportChangeRecords] = useState([]);
    const [transportHistoryLoading, setTransportHistoryLoading] = useState(false);

    // 交通变更提示显示状态
    const [showChangeAlert, setShowChangeAlert] = useState({});

    // 编辑身份证号码弹窗
    const [isEditIdCardModalVisible, setIsEditIdCardModalVisible] = useState(false);
    const [editingGuest, setEditingGuest] = useState(null);

    // 编辑联系人弹窗
    const [isEditContactModalVisible, setIsEditContactModalVisible] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [contactForm] = Form.useForm();

    // 编辑特别提醒弹窗
    const [isEditRemindModalVisible, setIsEditRemindModalVisible] = useState(false);
    const [editingRemindGuest, setEditingRemindGuest] = useState(null);
    const [editingRemindText, setEditingRemindText] = useState('');
    const [savingRemind, setSavingRemind] = useState(false);

    // 全屏状态
    const [isFullscreen, setIsFullscreen] = useState(false);

    // tableScrollY 三件套
    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const openHotelManagement = () => {
        setHotelDrawerVisible(true);
    };

    const closeHotelManagement = () => {
        setHotelDrawerVisible(false);
        // 更新酒店列表 - 实时获取最新酒店数据
        loadHotelList();
    };

    // 刷新列表
    const handleRefresh = () => {
        getExpertList();
    };

    // 获取会议项目信息
    const getConferenceInfo = async conferenceId => {
        const res = await conferenceOnceApi({ id: conferenceId });
        if (res.status_code === 200) {
            setConference(res.data);

            const dates = res.data.dates ? res.data.dates.split(',') : [];
            const datesArr = [];
            dates.forEach(item => {
                datesArr.push({ value: `${item} 00:00:00,${item} 11:59:59,AM`, label: `${dayjs(item).format('YYYY年MM月DD日')} 上午` });
                datesArr.push({ value: `${item} 12:00:00,${item} 23:59:59,PM`, label: `${dayjs(item).format('YYYY年MM月DD日')} 下午` });
            });
            setDateOptions(datesArr);
        }
    };

    // 检索
    const onFinish = values => {
        const newValues = dealDateParams(values);

        const newFilter = {
            ...filter,
            ...newValues,
            page: 1,
        };

        // 兼容接口未处理 [] 的情况
        if (isEmpty(newFilter?.roles)) {
            delete newFilter.roles;
        }
        if (isEmpty(newFilter?.expertLevels)) {
            delete newFilter.expertLevels;
        }
        dispatch(setFilter(newFilter));
    };

    // 重置
    const onReset = () => {
        form.resetFields();
        // 重置酒店选择相关状态
        setSelectedHotelId(null);
        setHotelRoomTypeOptions([]);

        const { collapsed, filter: initialFilter } = conferenceSlice.getInitialState().guestJourney;
        dispatch(setGuestJourneyCollapsed(collapsed));
        dispatch(setFilter({ ...initialFilter }));
    };

    // 传参、回显时间范围
    // 预览行程单
    const previewGuestJourney = record => {
        window.open(`${config.USERCENTER_PREFIX}/m/journey?conferenceId=${conferenceId}&expertId=${record.id}&title=${encodeURIComponent(conference?.name)}`);
    };

    const dealDateParams = values => {
        const newValues = { ...values };

        // 任务开始时间
        newValues.taskBeginTimeBegin = values.taskBeginTime && dayjs(values.taskBeginTime[0]).isValid() ? dayjs(values.taskBeginTime[0]).format('YYYY-MM-DD HH:mm') : undefined;
        newValues.taskBeginTimeEnd = values.taskBeginTime && dayjs(values.taskBeginTime[1]).isValid() ? dayjs(values.taskBeginTime[1]).format('YYYY-MM-DD HH:mm') : undefined;
        delete newValues.taskBeginTime;

        // 任务结束时间
        newValues.taskEndTimeBegin = values.taskEndTime && dayjs(values.taskEndTime[0]).isValid() ? dayjs(values.taskEndTime[0]).format('YYYY-MM-DD HH:mm') : undefined;
        newValues.taskEndTimeEnd = values.taskEndTime && dayjs(values.taskEndTime[1]).isValid() ? dayjs(values.taskEndTime[1]).format('YYYY-MM-DD HH:mm') : undefined;
        delete newValues.taskEndTime;

        // 来程交通时间（抵达时间）
        newValues.arrivalDatetimeBegin = values.arrivalTransportTime && dayjs(values.arrivalTransportTime[0]).isValid() ? dayjs(values.arrivalTransportTime[0]).format('YYYY-MM-DD HH:mm') : undefined;
        newValues.arrivalDatetimeEnd = values.arrivalTransportTime && dayjs(values.arrivalTransportTime[1]).isValid() ? dayjs(values.arrivalTransportTime[1]).format('YYYY-MM-DD HH:mm') : undefined;
        delete newValues.arrivalTransportTime;

        // 返程交通时间（出发时间）
        newValues.returnDatetimeBegin = values.returnTransportTime && dayjs(values.returnTransportTime[0]).isValid() ? dayjs(values.returnTransportTime[0]).format('YYYY-MM-DD HH:mm') : undefined;
        newValues.returnDatetimeEnd = values.returnTransportTime && dayjs(values.returnTransportTime[1]).isValid() ? dayjs(values.returnTransportTime[1]).format('YYYY-MM-DD HH:mm') : undefined;
        delete newValues.returnTransportTime;

        // 来程用车时间
        newValues.arrivalCarDatetimeBegin = values.arrivalCarTime && dayjs(values.arrivalCarTime[0]).isValid() ? dayjs(values.arrivalCarTime[0]).format('YYYY-MM-DD HH:mm') : undefined;
        newValues.arrivalCarDatetimeEnd = values.arrivalCarTime && dayjs(values.arrivalCarTime[1]).isValid() ? dayjs(values.arrivalCarTime[1]).format('YYYY-MM-DD HH:mm') : undefined;
        delete newValues.arrivalCarTime;

        // 返程用车时间
        newValues.returnCarDatetimeBegin = values.returnCarTime && dayjs(values.returnCarTime[0]).isValid() ? dayjs(values.returnCarTime[0]).format('YYYY-MM-DD HH:mm') : undefined;
        newValues.returnCarDatetimeEnd = values.returnCarTime && dayjs(values.returnCarTime[1]).isValid() ? dayjs(values.returnCarTime[1]).format('YYYY-MM-DD HH:mm') : undefined;
        delete newValues.returnCarTime;

        // 入住日期
        newValues.checkinDateBegin = values.checkinDate && dayjs(values.checkinDate[0]).isValid() ? dayjs(values.checkinDate[0]).format('YYYY-MM-DD') : undefined;
        newValues.checkinDateEnd = values.checkinDate && dayjs(values.checkinDate[1]).isValid() ? dayjs(values.checkinDate[1]).format('YYYY-MM-DD') : undefined;
        delete newValues.checkinDate;

        // 退房日期
        newValues.checkoutDateBegin = values.checkoutDate && dayjs(values.checkoutDate[0]).isValid() ? dayjs(values.checkoutDate[0]).format('YYYY-MM-DD') : undefined;
        newValues.checkoutDateEnd = values.checkoutDate && dayjs(values.checkoutDate[1]).isValid() ? dayjs(values.checkoutDate[1]).format('YYYY-MM-DD') : undefined;
        delete newValues.checkoutDate;

        return newValues;
    };

    // 嘉宾身份
    const getGuestIdentity = role => {
        if (role === 0) {
            return '待定';
        }
        if (role === 1) {
            return '参会嘉宾';
        }
        if (role === 2) {
            return '参会代表';
        }
        if (role === 3) {
            return '企业人员';
        }
        if (role === 4) {
            return '工作人员';
        }
    };

    // 加载酒店列表
    const loadHotelList = async () => {
        try {
            const res = await getHotelListDayApi({ conferenceId });
            if (res.status_code === 200 && res.data) {
                const hotels = res.data.map(hotel => ({
                    value: hotel.id,
                    label: hotel.name,
                    roomTypes: hotel.roomTypes || [],
                    roomTypeDays: hotel.roomTypeDays || [],
                }));
                setHotelOptions(hotels);
            }
        } catch (error) {
            console.error('加载酒店列表失败:', error);
        }
    };

    // 处理酒店选择变化
    const handleHotelChange = hotelId => {
        setSelectedHotelId(hotelId);

        if (hotelId) {
            const selectedHotel = hotelOptions.find(h => h.value === hotelId);
            if (selectedHotel && selectedHotel.roomTypes) {
                const roomTypes = selectedHotel.roomTypes.map(rt => ({
                    value: rt.id,
                    label: rt.roomType,
                }));
                setHotelRoomTypeOptions(roomTypes);
            } else {
                setHotelRoomTypeOptions([]);
            }
        } else {
            setHotelRoomTypeOptions([]);
            form.setFieldsValue({ hotelRoomTypeId: undefined });
        }
    };

    const handleToggleCollapsed = () => {
        dispatch(setGuestJourneyCollapsed(!collapsed));
    };

    // 嘉宾列表
    const getExpertList = async () => {
        setLoading(true);

        try {
            const params = {
                conferenceId,
                page: filter.page || 1,
                pageSize: filter.pageSize || 20,
                ...filter,
            };

            const res = await guestjourneysApi(params);

            if (res.status_code === 200 && res.data) {
                const list = res.data.list || [];
                setData(list.map(item => ({ ...item, key: item.id })));
                setTotal(res.data.totalCount || 0);
            } else {
                message.error(res.message || '获取嘉宾列表失败');
                setData([]);
                setTotal(0);
            }
        } catch (error) {
            console.error('获取嘉宾列表失败:', error);
            message.error('获取嘉宾列表失败');
            setData([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    const toggleAgendaTasks = guestAgendaInfo => {
        setData(
            data.map(item => {
                if (item.id === guestAgendaInfo.id) {
                    return { ...item, expanded: !item.expanded };
                } else {
                    return item;
                }
            }),
        );
    };

    const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
    const canPreviewGuestJourney = roleList.some(item => item.name === '嘉宾行程管理');

    const columns = [
        {
            title: '嘉宾基本信息',
            dataIndex: 'guestInfo',
            width: 300,
            render: (_, record) => {
                // 获取性别图标颜色：默认蓝色，女性黄色
                const iconColorClass = record.memGender === 0 ? 'female' : 'male';
                // 获取专家等级标签
                const expertLevelLabel = expertLevels.find(a => a.level === record.expertLevel)?.label || '';
                // 获取身份标签
                const roleLabel = getGuestIdentity(record?.guestsEntity?.role) || '';

                return (
                    <div className="guest-info-cell">
                        <div className="guest-name-row">
                            <span className="name-text">姓名：{record.memUsername}</span>
                            {(record.memGender === 0 || record.memGender === 1) && <i className={`bi bi-person-fill gender-icon ${iconColorClass}`}></i>}
                        </div>
                        <div className="guest-details">
                            <div className="detail-line">
                                单位：
                                <WorkExperiences workExperiences={record.ssoMemberWorkExperiencesEntities} layout="inline" />
                            </div>
                            <div className="detail-line">
                                手机：
                                {!record?.memMobile || record?.memMobile?.startsWith('100') ? <span className="no-data">暂无</span> : record.memMobile}
                            </div>
                            <div className="detail-line">
                                证件：
                                {record.idCard ? (
                                    <>
                                        <span>
                                            {cardTypes.find(t => t.code === record.idcardType)?.name} {record.idCardStr}
                                        </span>
                                        <Button type="link" icon={<i className="bi bi-pencil"></i>} onClick={() => handleEditIdCard(record)} style={{ padding: '0 5px' }} />
                                    </>
                                ) : (
                                    <>
                                        <span className="no-data">暂无</span>
                                        <Button type="link" icon={<i className="bi bi-pencil"></i>} onClick={() => handleEditIdCard(record)} style={{ padding: '0 5px' }} />
                                    </>
                                )}
                            </div>
                            <div className="detail-line">
                                级别：<span className="line-level">{expertLevelLabel}</span>
                            </div>
                            <div className="detail-line">身份：{roleLabel}</div>
                        </div>
                    </div>
                );
            },
        },
        {
            title: '会议任务',
            dataIndex: 'conferenceTask',
            width: 331,
            render: (_, record) => <GuestTaskList agendasGuestsList={record.agendasGuestsList} expanded={record.expanded} onToggleExpansion={toggleAgendaTasks} record={record} />,
        },
        {
            title: '来程信息',
            dataIndex: 'arrivalInfo',
            width: 350,
            render: (_, record) => {
                // 解析来程交通和用车信息（驼峰命名）
                let arrivalTransport = {};
                let arrivalCar = {};

                try {
                    if (record?.guestJourneysEntity?.arrivalTransport) {
                        arrivalTransport = JSON.parse(record.guestJourneysEntity.arrivalTransport);
                    }
                } catch (e) {
                    console.error('解析来程交通信息失败:', e);
                }

                try {
                    if (record?.guestJourneysEntity?.arrivalCar) {
                        arrivalCar = JSON.parse(record.guestJourneysEntity.arrivalCar);
                    }
                } catch (e) {
                    console.error('解析来程用车信息失败:', e);
                }

                // 构建来程信息对象
                const arrivalInfo = {
                    transport: arrivalTransport,
                    car: arrivalCar,
                };

                return (
                    <JourneyInfoDisplay
                        type="arrival"
                        journeyInfo={arrivalInfo}
                        systemTime={getSuggestedTime(record.agendasGuestsList, 'arrival')}
                        showChangeAlert={showChangeAlert[record.key]?.arrival}
                        onCloseAlert={() => handleCloseChangeAlert(record.key, 'arrival')}
                        onTicketStatusChange={(index, newStatus) => handleTicketStatusChange(record.key, 'arrival', index, newStatus)}
                        onEditTransport={() => handleEditTransport(record.key, 'arrival')}
                        onEditCar={type => handleEditCar(record.key, type)}
                        onViewTransportHistory={() => handleViewTransportHistory(record.key, 'arrival')}
                    />
                );
            },
        },
        {
            title: '返程信息',
            dataIndex: 'returnInfo',
            width: 350,
            render: (_, record) => {
                // 解析返程交通和用车信息（驼峰命名）
                let returnTransport = {};
                let returnCar = {};

                try {
                    if (record?.guestJourneysEntity?.returnTransport) {
                        returnTransport = JSON.parse(record.guestJourneysEntity.returnTransport);
                    }
                } catch (e) {
                    console.error('解析返程交通信息失败:', e);
                }

                try {
                    if (record?.guestJourneysEntity?.returnCar) {
                        returnCar = JSON.parse(record.guestJourneysEntity.returnCar);
                    }
                } catch (e) {
                    console.error('解析返程用车信息失败:', e);
                }

                // 构建返程信息对象
                const returnInfo = {
                    transport: returnTransport,
                    car: returnCar,
                };

                return (
                    <JourneyInfoDisplay
                        type="return"
                        journeyInfo={returnInfo}
                        systemTime={getSuggestedTime(record.agendasGuestsList, 'return')}
                        showChangeAlert={showChangeAlert[record.key]?.return}
                        onCloseAlert={() => handleCloseChangeAlert(record.key, 'return')}
                        onTicketStatusChange={(index, newStatus) => handleTicketStatusChange(record.key, 'return', index, newStatus)}
                        onEditTransport={() => handleEditTransport(record.key, 'return')}
                        onEditCar={type => handleEditCar(record.key, type)}
                        onViewTransportHistory={() => handleViewTransportHistory(record.key, 'return')}
                    />
                );
            },
        },
        {
            title: '住宿信息',
            dataIndex: 'hotelInfo',
            width: 300,
            render: (_, record) => {
                // 解析住宿信息
                let hotelInfo = {};

                try {
                    if (record?.guestJourneysEntity?.hotelInfo) {
                        hotelInfo = JSON.parse(record.guestJourneysEntity.hotelInfo);
                    }
                } catch (e) {
                    console.error('解析住宿信息失败:', e);
                }

                // 获取建议入住日期（第一个已确认任务的日期）
                const getSuggestedCheckInDate = () => {
                    if (!record.agendasGuestsList || record.agendasGuestsList.length === 0) {
                        return undefined;
                    }

                    // 找到第一个已确认的任务
                    for (const task of record.agendasGuestsList) {
                        if (task.invitationAccepted === 1) {
                            return dayjs(task.startTime).format('M月D日');
                        }
                    }
                    return undefined;
                };

                // 获取建议退房日期（最后一个已确认任务的日期）
                const getSuggestedCheckOutDate = checkInDate => {
                    if (!record.agendasGuestsList || record.agendasGuestsList.length === 0) {
                        return undefined;
                    }

                    let lastConfirmedDate;

                    // 从后往前遍历，找到最后一个已确认的任务
                    for (let i = record.agendasGuestsList.length - 1; i >= 0; i--) {
                        const task = record.agendasGuestsList[i];
                        if (task.invitationAccepted === 1) {
                            lastConfirmedDate = dayjs(task.endTime).format('M月D日');
                            break;
                        }
                    }

                    if (!lastConfirmedDate) {
                        return undefined;
                    }

                    // 如果入住和退房是同一天，退房日期取第二天
                    if (checkInDate && lastConfirmedDate === checkInDate) {
                        const dateMatch = lastConfirmedDate.match(/(\d+)月(\d+)日/);
                        if (dateMatch) {
                            const month = dateMatch[1];
                            const day = parseInt(dateMatch[2]);
                            return `${month}月${day + 1}日`;
                        }
                    }

                    return lastConfirmedDate;
                };

                const suggestedCheckIn = getSuggestedCheckInDate();
                const suggestedCheckOut = getSuggestedCheckOutDate(suggestedCheckIn);

                return <HotelInfoDisplay hotelInfo={hotelInfo} suggestedCheckIn={suggestedCheckIn} suggestedCheckOut={suggestedCheckOut} hotelOptions={hotelOptions} onEditHotel={() => handleEditHotel(record.key)} />;
            },
        },
        {
            title: (
                <span className="remind-column-title">
                    特别提醒
                    <Tooltip title={REMIND_PREVIEW_TIP}>
                        <i className="bi bi-question-circle remind-title-icon" />
                    </Tooltip>
                </span>
            ),
            dataIndex: 'remind',
            width: 240,
            render: (_, record) => {
                const remindText = record?.guestJourneysEntity?.remind;
                const hasRemind = !!remindText;

                return (
                    <div className="remind-cell">
                        {hasRemind && (
                            <Alert
                                message={remindText.split('\n').map((line, index) => (
                                    <span key={index}>
                                        {line}
                                        {index < remindText.split('\n').length - 1 && <br />}
                                    </span>
                                ))}
                                type="warning"
                                showIcon={false}
                                banner={false}
                                className="remind-alert"
                            />
                        )}
                        <Button type="link" size="small" icon={<i className="bi bi-pencil"></i>} onClick={() => handleEditRemind(record)}>
                            编辑
                        </Button>
                    </div>
                );
            },
        },
        {
            title: '分配服务商',
            dataIndex: 'partners',
            width: 200,
            render: (_, record) => {
                // 获取服务商ID
                const transportPartnerId = record?.guestsEntity?.transportPartnerId;
                const carPartnerId = record?.guestsEntity?.carPartnerId;
                const hotelPartnerId = record?.guestsEntity?.hotelPartnerId;

                // 查找服务商名称
                const getPartnerName = (partnerId, vendorList) => {
                    if (!partnerId) return null;
                    const partner = vendorList.find(v => v.value === partnerId);
                    return partner?.label || null;
                };

                const transportPartnerName = getPartnerName(transportPartnerId, transportVendorOptions);
                const carPartnerName = getPartnerName(carPartnerId, carVendorOptions);
                const hotelPartnerName = getPartnerName(hotelPartnerId, hotelVendorOptions);

                return (
                    <div className="partner-info-cell">
                        <div className="partner-item">
                            <span className="partner-label">交通：</span>
                            {transportPartnerName ? <span>{transportPartnerName}</span> : <Text type="secondary">未授权</Text>}
                        </div>
                        <div className="partner-item">
                            <span className="partner-label">用车：</span>
                            {carPartnerName ? <span>{carPartnerName}</span> : <Text type="secondary">未授权</Text>}
                        </div>
                        <div className="partner-item">
                            <span className="partner-label">住宿：</span>
                            {hotelPartnerName ? <span>{hotelPartnerName}</span> : <Text type="secondary">未授权</Text>}
                        </div>
                    </div>
                );
            },
        },
        {
            title: '联系人',
            dataIndex: 'contacts',
            width: 200,
            render: (_, record) => {
                // 解析联系人信息
                let contacts = [];

                try {
                    if (record?.guestJourneysEntity?.contracts) {
                        contacts = JSON.parse(record.guestJourneysEntity.contracts);
                    }
                } catch (e) {
                    console.error('解析联系人信息失败:', e);
                }

                return (
                    <div className="contacts-info-cell">
                        {contacts.map((contact, index) => (
                            <div key={index}>
                                <span className="contact-name">{`${contact.name} `}</span>
                                <span className="contact-phone">{contact.phone}</span>
                            </div>
                        ))}
                        <Button type="link" icon={<i className="bi bi-pencil"></i>} onClick={() => handleEditContact(record, contacts)} style={{ padding: '0 5px' }}>
                            编辑
                        </Button>
                    </div>
                );
            },
        },
        ...(canPreviewGuestJourney
            ? [
                  {
                      title: '操作',
                      dataIndex: 'operations',
                      width: 120,
                      fixed: 'right',
                      render: (_, record) => {
                          return (
                              <>
                                  <Tooltip title={record.guestJourneysEntity ? '' : '暂无行程信息'}>
                                      <Space>
                                          <Button size="small" type="link" disabled={!record.guestJourneysEntity} onClick={() => previewGuestJourney(record)}>
                                              预览行程单
                                          </Button>
                                      </Space>
                                  </Tooltip>
                              </>
                          );
                      },
                  },
              ]
            : []),
    ];

    const addAuthColumns = [
        {
            title: '序号',
            dataIndex: 'index',
            width: 80,
            render: (_, record, index) => {
                return index + 1;
            },
        },
        {
            title: '服务商名称',
            dataIndex: 'partnerName',
            width: 200,
        },
        {
            title: '权限',
            dataIndex: 'permissions',
            width: 250,
            render: permissions => {
                const permissionLabels = {
                    transport_mgmt: '往返交通管理',
                    car_mgmt: '往返用车管理',
                    hotel_mgmt: '酒店住宿管理',
                };
                const labels = (permissions || []).map(p => permissionLabels[p] || p);
                return labels.join('、');
            },
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 150,
            render: (_, record) => {
                return (
                    <>
                        <Button size="small" type="link" onClick={() => editAuth(record)}>
                            编辑
                        </Button>
                        <Button size="small" type="link" onClick={() => deleteAuth(record)}>
                            移除
                        </Button>
                    </>
                );
            },
        },
    ];

    const guestSelection = {
        selectedRowKeys: selectedExpertsIds,
        preserveSelectedRowKeys: true,
        onChange: (selectedRowKeys, selectedRows) => {
            if (selectedRows.length > 0) {
                setSelectedExpertsIds([...selectedRowKeys]);
            } else {
                setSelectedExpertsIds([]);
            }
        },
    };

    // 切换页码
    const onChangePage = (page, pageSize) => {
        dispatch(
            setFilter({
                ...filter,
                page,
                pageSize,
            }),
        );
    };

    // 行程单设置
    const settingGuestJourney = () => {
        setVisibleGuestJourneySetDrawer(true);
    };

    // 排序点击处理
    const onClickSortDropdown = ({ key }) => {
        const newOrderMode = Number(key);
        // 如果点击的是当前排序方式，不重复请求
        if ((newOrderMode === 0 && !filter?.orderMode) || filter?.orderMode === newOrderMode) {
            return;
        }
        dispatch(
            setFilter({
                ...filter,
                orderMode: newOrderMode,
                page: 1,
            }),
        );
    };

    // 获取合计信息
    const getGuestJourneyTotal = async () => {
        const res = await GuestJourneyTotalApi({ conferenceId });
        if (res.status_code === 200 && res.data) {
            setGuestTotalInfo(res.data);
        }
    };

    const getDateRoom = days => {
        if (days && days.length) {
            return (
                <>
                    （
                    {days.map((item, index) => (
                        <span key={index}>
                            {item.day}住宿{item.num}人{index !== days.length - 1 ? '；' : ''}
                        </span>
                    ))}
                    ）
                </>
            );
        }
        return '';
    };

    // 会议论坛列表 - 筛选
    const prepareConferenceHallTreeData = data => {
        const treeData = [];

        if (Object.keys(data) && Object.keys(data).length) {
            Object.keys(data).forEach(agendaDate => {
                const dateNode = {
                    title: agendaDate,
                    value: agendaDate,
                    selectable: false,
                    hallNum: 0,
                    children: [],
                };

                const confHalls = data[agendaDate];
                confHalls
                    && confHalls.length
                    && confHalls.forEach(confHall => {
                        const hallNode = {
                            title: `${confHall.name} [YHYC#${confHall.id}]`,
                            value: confHall.id,
                            children: [],
                        };

                        const confSubHalls = confHall.secondHallsList || [];
                        if (confSubHalls && confSubHalls.length) {
                            hallNode.selectable = false;

                            confSubHalls.forEach(subHall => {
                                dateNode.hallNum += 1;

                                hallNode.children.push({
                                    title: `${subHall.name} [YHYC#${subHall.id}]`,
                                    value: subHall.id,
                                });
                            });
                        } else {
                            dateNode.hallNum += 1;
                        }

                        dateNode.children.push(hallNode);
                    });

                treeData.push(dateNode);
            });
        }
        return treeData;
    };

    // 授权服务商
    const authorizeVendors = async () => {
        setAuthServiceProviderOpen(true);
        // 加载授权服务商列表
        await loadAuthServiceProviders();
        // 加载授权截止日期
        if (conference?.partnerAuthDeadline) {
            setAuthDeadline(dayjs(conference.partnerAuthDeadline));
            authServiceForm.setFieldsValue({ deadline: dayjs(conference.partnerAuthDeadline) });
        }
    };

    // 加载授权服务商列表
    const loadAuthServiceProviders = async () => {
        try {
            // 不传 permission 参数，一次性获取所有授权服务商及其完整权限列表
            const res = await listWithConferenceInfoApi({ conferenceId });

            if (res.status_code === 200 && res.data && res.data.conferencePartners) {
                // 处理权限字段，将字符串转为数组
                const list = res.data.conferencePartners;
                const processedData = list.map(partner => ({
                    ...partner,
                    permissions: partner.permissions ? partner.permissions.split(',') : undefined,
                }));
                setAuthTableData(processedData);
            } else {
                setAuthTableData([]);
            }
        } catch (error) {
            message.error('加载授权服务商失败: ' + error.message);
        }
    };

    // 授权服务商确认
    const handleAuthServiceOk = async () => {};

    // 授权服务商取消
    const handleAuthServiceCancel = () => {
        setAuthServiceProviderOpen(false);
    };

    // 保存授权截止日期
    const saveAuthDeadline = () => {
        authServiceForm
            .validateFields(['deadline'])
            .then(async values => {
                try {
                    // 调用API保存授权截止日期
                    const requestData = {
                        id: conferenceId,
                        partnerAuthDeadline: values.deadline ? dayjs(values.deadline).format('YYYY-MM-DD') : null,
                    };

                    const res = await updatePartnerDeadlineApi(requestData);
                    if (res.status_code === 200) {
                        setAuthDeadline(values.deadline);
                        setIsEditingDeadline(false);
                        message.success('授权截止日期保存成功');
                        // 刷新会议信息
                        getConferenceInfo(conferenceId);
                    } else {
                        message.error('保存失败: ' + res.message);
                    }
                } catch (error) {
                    message.error('保存失败: ' + error.message);
                }
            })
            .catch(errorInfo => {
                console.log('验证失败:', errorInfo);
            });
    };

    // 取消编辑授权截止日期
    const cancelEditAuthDeadline = () => {
        authServiceForm.setFieldsValue({ deadline: authDeadline });
        setIsEditingDeadline(false);
    };

    // 编辑授权
    const editAuth = record => {
        setEditingAuth(record);
        editAuthForm.setFieldsValue({
            permissions: record.permissions || undefined,
        });
        setAddAuthServiceOpen(true);
    };

    // 删除授权
    const deleteAuth = record => {
        Modal.confirm({
            title: '提示',
            content: '若已有专家授权了该服务商,其相关授权将被同步清空。请在确认本操作后,重新为专家授权服务商。',
            okText: '确定',
            cancelText: '取消',
            onOk: async () => {
                try {
                    const res = await deleteConferencePartnersApi({
                        conferenceId,
                        id: record.id,
                        partnerId: record.partnerId,
                    });
                    if (res.status_code === 200) {
                        message.success('删除成功');
                        // 重新加载列表
                        loadAuthServiceProviders();
                    } else {
                        message.error('删除失败: ' + res.message);
                    }
                } catch (error) {
                    message.error('删除失败: ' + error.message);
                }
            },
        });
    };

    const handleAddAuthOk = async () => {
        const form = editingAuth ? editAuthForm : addAuthServiceForm;

        try {
            const values = await form.validateFields();

            if (editingAuth) {
                // 编辑模式
                const res = await updateConferencePartnersApi({
                    id: editingAuth.id,
                    permissions: values.permissions.join(','),
                });

                if (res.status_code === 200) {
                    message.success('编辑成功');
                    setAddAuthServiceOpen(false);
                    setEditingAuth(null);
                    editAuthForm.resetFields();
                    // 重新加载列表
                    loadAuthServiceProviders();
                } else {
                    message.error('编辑失败: ' + res.message);
                }
            } else {
                // 新增模式
                const res = await saveConferencePartnersApi({
                    conferenceId,
                    partnerList: values.partnerList,
                    permissions: values.permissions.join(','),
                });

                if (res.status_code === 200) {
                    message.success('新增成功');
                    setAddAuthServiceOpen(false);
                    addAuthServiceForm.resetFields();
                    setVendorSearchValue('');
                    // 重新加载列表
                    loadAuthServiceProviders();
                } else {
                    message.error('新增失败: ' + res.message);
                }
            }
        } catch (error) {
            console.error('表单验证失败:', error);
        }
    };

    const handleAddAuthCancel = () => {
        setAddAuthServiceOpen(false);
        setEditingAuth(null);
        addAuthServiceForm.resetFields();
        editAuthForm.resetFields();
        setVendorSearchValue('');
    };

    // 新增授权
    const addAuth = () => {
        setEditingAuth(null);
        addAuthServiceForm.resetFields();
        setAddAuthServiceOpen(true);
    };

    // 搜索服务商
    const handleSearchVendor = async value => {
        setVendorSearchValue(value);
        if (!value) {
            setVendorOptions([]);
            return;
        }

        try {
            const res = await getPartnersApi({
                conferenceId,
                name: value,
            });

            if (res.status_code === 200 && res.data) {
                setVendorOptions(
                    res.data.map(item => ({
                        value: item.id,
                        label: item.name,
                    })),
                );
            }
        } catch (error) {
            console.error('搜索服务商失败:', error);
        }
    };

    // 获取会议列表
    const getConferenceList = async () => {
        const res = await getConferenceListApi({ conferenceId });
        if (res.status_code === 200 && res.data) {
            const agendaData = res.data;
            setConferenceHallTreeData(prepareConferenceHallTreeData(agendaData));
        }
    };

    // 关闭交通变更提示
    const handleCloseChangeAlert = guestKey => {
        setShowChangeAlert(prev => ({
            ...prev,
            [guestKey]: false,
        }));
    };

    // 打开变更行程交通弹窗
    const handleEditTransport = (guestKey, type) => {
        // 获取嘉宾的原有行程数据
        const guest = data.find(g => g.key === guestKey);

        // 解析交通信息
        let transportInfo = {};
        try {
            const transportField = type === 'arrival' ? 'arrivalTransport' : 'returnTransport';
            if (guest?.guestJourneysEntity?.[transportField]) {
                transportInfo = JSON.parse(guest.guestJourneysEntity[transportField]);
            }
        } catch (e) {
            console.error('解析交通信息失败:', e);
        }

        // 传递给编辑弹窗
        setEditingJourneyInfo({
            guestKey,
            type,
            initialData: transportInfo, // 直接传递解析后的数据
        });
        setIsJourneyEditModalVisible(true);
    };

    // 关闭变更行程交通弹窗
    const handleCancelJourneyEdit = () => {
        setIsJourneyEditModalVisible(false);
        setEditingJourneyInfo(null);
    };

    // 格式化变更时间
    const formatChangeTime = dateTimeStr => {
        if (!dateTimeStr) return '';
        try {
            const date = dayjs(dateTimeStr);
            if (date.isValid()) {
                return date.format('YYYY年 MM月DD日 HH:mm:ss');
            }
            return '';
        } catch (error) {
            console.warn('格式化时间失败:', dateTimeStr, error);
            return '';
        }
    };

    // 转换交通信息为组件需要的格式
    const convertTransportInfo = transport => {
        // 处理出发日期时间
        let departureDate = '';
        let departureTime = '';
        if (transport.departure_datetime) {
            const departureStr = String(transport.departure_datetime).trim();
            // 如果已经是格式化好的（如 "11月12日 10:00"），直接提取
            if (departureStr.includes('月') && departureStr.includes('日')) {
                const dateMatch = departureStr.match(/(\d{1,2})月(\d{1,2})日/);
                const timeMatch = departureStr.match(/(\d{1,2}:\d{2})/);
                if (dateMatch) {
                    departureDate = `${dateMatch[1]}月${dateMatch[2]}日`;
                }
                if (timeMatch) {
                    departureTime = timeMatch[1];
                }
            } else {
                // 处理 "2024-11-2309:30" 格式（日期和时间连在一起）
                const compactMatch = departureStr.match(/^(\d{4}-\d{2}-\d{2})(\d{1,2}:\d{2}(?::\d{2})?)$/);
                if (compactMatch) {
                    const [, datePart, timePart] = compactMatch;
                    const normalizedDateTime = `${datePart} ${timePart}`;
                    const departureDateObj = dayjs(normalizedDateTime);
                    if (departureDateObj.isValid()) {
                        departureDate = departureDateObj.format('MM月DD日');
                        departureTime = departureDateObj.format('HH:mm');
                    }
                } else {
                    // 标准格式，使用 dayjs 解析
                    const departureDateObj = dayjs(departureStr);
                    if (departureDateObj.isValid()) {
                        departureDate = departureDateObj.format('MM月DD日');
                        departureTime = departureDateObj.format('HH:mm');
                    }
                }
            }
        }

        // 处理到达时间
        let arrivalTime = '';
        if (transport.arrival_datetime) {
            const arrivalStr = String(transport.arrival_datetime).trim();
            // 如果已经是格式化好的（如 "11月12日 10:00"），直接提取时间
            if (arrivalStr.includes('月') && arrivalStr.includes('日')) {
                const timeMatch = arrivalStr.match(/(\d{1,2}:\d{2})/);
                if (timeMatch) {
                    arrivalTime = timeMatch[1];
                }
            } else {
                // 处理 "2024-11-2309:30" 格式（日期和时间连在一起）
                const compactMatch = arrivalStr.match(/^(\d{4}-\d{2}-\d{2})(\d{1,2}:\d{2}(?::\d{2})?)$/);
                if (compactMatch) {
                    const [, datePart, timePart] = compactMatch;
                    const normalizedDateTime = `${datePart} ${timePart}`;
                    const arrivalDateObj = dayjs(normalizedDateTime);
                    if (arrivalDateObj.isValid()) {
                        arrivalTime = arrivalDateObj.format('HH:mm');
                    }
                } else {
                    // 标准格式，使用 dayjs 解析
                    const arrivalDateObj = dayjs(arrivalStr);
                    if (arrivalDateObj.isValid()) {
                        arrivalTime = arrivalDateObj.format('HH:mm');
                    }
                }
            }
        }

        return {
            type: transport.transport_type_text || '飞机',
            number: transport.transport_no || '',
            cabin: transport.seat_type || '',
            from: transport.departure_place || '',
            to: transport.arrival_place || '',
            date: departureDate,
            departTime: departureTime,
            arriveTime: arrivalTime,
            ticketStatus: transport.ticket_ready === 1 || transport.ticket_ready === true ? '已出票' : '未出票',
        };
    };

    // 从 dataSnapshot 中提取变更前的交通信息
    const extractOldTransportsFromSnapshot = dataSnapshot => {
        if (!dataSnapshot) return [];
        try {
            const snapshot = typeof dataSnapshot === 'string' ? JSON.parse(dataSnapshot) : dataSnapshot;
            // dataSnapshot 可能包含 transports 字段，表示变更前的交通信息
            if (snapshot.transports && Array.isArray(snapshot.transports)) {
                return snapshot.transports.map(convertTransportInfo);
            }
            return [];
        } catch (error) {
            console.warn('解析 dataSnapshot 失败:', error);
            return [];
        }
    };

    // 从 extra 数据中收集交通信息，并根据操作类型动态设置出票状态
    const collectTransports = (log, extraData) => {
        const transports = [];

        // 从 transportInfo 中收集
        if (extraData.transportInfo) {
            const { transportInfo } = extraData;
            if (Array.isArray(transportInfo.transports)) {
                transportInfo.transports.forEach(item => {
                    transports.push({
                        ...item,
                        // 根据操作类型动态设置出票状态
                        ticket_ready: log.operationType === 'issue_ticket' ? 1 : log.operationType === 'cancel_ticket' ? 0 : item.ticket_ready,
                    });
                });
            } else if (transportInfo.transport_no || transportInfo.transport_type_text) {
                transports.push({
                    ...transportInfo,
                    ticket_ready: log.operationType === 'issue_ticket' ? 1 : log.operationType === 'cancel_ticket' ? 0 : transportInfo.ticket_ready,
                });
            }
        }

        // 从 transports 数组中收集
        if (Array.isArray(extraData.transports)) {
            extraData.transports.forEach(item => {
                // 检查是否已经存在相同的交通信息
                const isDuplicate = transports.some(t => t.transport_type === item.transport_type && t.transport_no === item.transport_no && t.seat_type === item.seat_type);
                if (!isDuplicate) {
                    transports.push({
                        ...item,
                        ticket_ready: log.operationType === 'issue_ticket' ? 1 : log.operationType === 'cancel_ticket' ? 0 : item.ticket_ready,
                    });
                }
            });
        }

        return transports;
    };

    // 打开交通变更记录弹窗并获取数据
    const handleViewTransportHistory = async (guestKey, type) => {
        setViewingTransportHistory({ guestKey, type });
        setIsTransportHistoryModalVisible(true);
        setTransportChangeRecords([]);
        setTransportHistoryLoading(true);

        try {
            if (!conferenceId) {
                message.error('会议ID不存在');
                setTransportHistoryLoading(false);
                return;
            }

            const operationType = type === 'arrival' ? 'change_arrival_transport' : 'change_return_transport';
            const expertId = parseInt(guestKey);

            const params = {
                module: 'guest_journey',
                portal: 'boss,partner',
                entityType: 'conference',
                entityId: parseInt(conferenceId),
                operationType,
                conditions: [{ path: 'expertInfo.expertId', search_str: expertId }],
            };

            const res = await getOperationLogListWithExtra(params);

            if (res.status_code === 200 || res.code === 200) {
                const logRecords = Array.isArray(res.data) ? res.data : res.data?.records || res.data?.list || [];

                // 按时间倒序排列（最新的在前）
                const sortedLogs = logRecords.sort((a, b) => {
                    const timeA = new Date(a.createdAt || 0).getTime();
                    const timeB = new Date(b.createdAt || 0).getTime();
                    return timeB - timeA;
                });

                // 转换为组件需要的格式
                const changeRecords = sortedLogs.map((log, index) => {
                    // 解析 extra 字段
                    let extraData = {};
                    try {
                        extraData = typeof log.extra === 'string' ? JSON.parse(log.extra) : log.extra || {};
                    } catch (error) {
                        console.warn('解析 extra 失败:', error);
                    }

                    // 变更后的交通信息（根据操作类型动态设置出票状态）
                    const sourceTransports = collectTransports(log, extraData);
                    const newTransports = sourceTransports.map(convertTransportInfo);

                    // 变更前的交通信息
                    // 优先从 dataSnapshot 获取，如果没有则从上一条记录获取变更后的信息作为当前记录的变更前信息
                    let oldTransports = extractOldTransportsFromSnapshot(log.dataSnapshot);

                    // 如果 dataSnapshot 中没有，且不是第一条记录，则从上一条记录获取变更后的信息
                    if (oldTransports.length === 0 && index < sortedLogs.length - 1) {
                        const prevLog = sortedLogs[index + 1];
                        let prevExtraData = {};
                        try {
                            prevExtraData = typeof prevLog.extra === 'string' ? JSON.parse(prevLog.extra) : prevLog.extra || {};
                        } catch (error) {
                            console.warn('解析上一条记录的 extra 失败:', error);
                        }
                        const prevTransportsData = prevExtraData.transportInfo?.transports || prevExtraData.transports || [];
                        oldTransports = prevTransportsData.map(convertTransportInfo);
                    }

                    const operator = (() => {
                        if (log.portal === 'boss') {
                            return `健康界平台 ${log.adminName || ''}`.trim();
                        }

                        // 服务商端（partner）
                        const partnerInfo = extraData.partnerInfo || {};
                        if (log.portal === 'partner') {
                            const partnerName = partnerInfo.partner_name || partnerInfo.partnerName || '服务商';
                            const partnerUserName = partnerInfo.partner_user_name || partnerInfo.partnerUserName || partnerInfo.user_name || partnerInfo.userName || '';
                            return `${partnerName} ${partnerUserName}`.trim();
                        }

                        return log.adminName || '';
                    })();

                    return {
                        changeTime: formatChangeTime(log.createdAt),
                        changeReason: extraData.reason || '',
                        newTransports,
                        oldTransports,
                        operator,
                    };
                });

                setTransportChangeRecords(changeRecords);
            } else {
                console.error('获取交通变更记录失败:', res);
                message.error('获取交通变更记录失败');
                setTransportChangeRecords([]);
            }
        } catch (error) {
            console.error('获取交通变更记录失败:', error);
            message.error(`获取交通变更记录失败: ${error.message || '未知错误'}`);
            setTransportChangeRecords([]);
        } finally {
            setTransportHistoryLoading(false);
        }
    };

    // 打开住宿变更弹窗
    const handleEditHotel = guestKey => {
        // 获取嘉宾的原有住宿数据
        const guest = data.find(g => g.key === guestKey);
        const hotelInfo = guest?.guestJourneysEntity?.hotelInfo ? JSON.parse(guest.guestJourneysEntity.hotelInfo) : null;

        // 设置正在编辑的住宿信息
        setEditingHotelInfo({ guestKey, initialData: hotelInfo });
        setIsHotelEditModalVisible(true);
    };

    // 关闭交通变更记录弹窗
    const handleCancelTransportHistory = () => {
        setIsTransportHistoryModalVisible(false);
        setViewingTransportHistory(null);
        setTransportChangeRecords([]);
    };

    // 打开编辑身份证号码弹窗
    const handleEditIdCard = guest => {
        setEditingGuest(guest);
        setIsEditIdCardModalVisible(true);
    };

    // 关闭编辑身份证号码弹窗
    const handleCancelIdCardEdit = () => {
        setIsEditIdCardModalVisible(false);
        setEditingGuest(null);
    };

    // 提交身份证号码修改
    const handleSubmitIdCardEdit = async values => {
        try {
            const res = await updateGuestJourneyIdCard({
                idcardType: values.idcardType,
                idCard: values.idCard,
                id: editingGuest.id,
            });
            if (res.status_code === 200) {
                getExpertList();
                handleCancelIdCardEdit();
            }
        } catch (error) {
            message.error('更新失败: ' + error.message);
        }
    };

    // 打开编辑联系人弹窗
    const handleEditContact = (guest, contacts) => {
        setEditingContact({ guest, contacts });
        // 设置表单初始值
        contactForm.setFieldsValue({
            contracts: contacts && contacts.length > 0 ? contacts : [],
        });
        setIsEditContactModalVisible(true);
    };

    // 关闭编辑联系人弹窗
    const handleCancelContactEdit = () => {
        setIsEditContactModalVisible(false);
        setEditingContact(null);
        contactForm.resetFields();
    };

    // 提交联系人修改
    const handleSubmitContactEdit = async () => {
        try {
            const values = await contactForm.validateFields();
            const contracts = values.contracts || [];

            // 调用保存接口
            const res = await saveGuestJourney({
                conferenceId: conferenceId,
                expertId: editingContact.guest.id,
                contracts: JSON.stringify(contracts),
            });

            if (res.status_code === 200) {
                message.success('联系人信息保存成功');
                getExpertList();
                handleCancelContactEdit();
            } else {
                message.error(res.message || '保存失败');
            }
        } catch (error) {
            console.error('保存联系人信息失败:', error);
        }
    };

    // 打开编辑特别提醒弹窗
    const handleEditRemind = record => {
        setEditingRemindGuest(record);
        setEditingRemindText(record?.guestJourneysEntity?.remind || '');
        setIsEditRemindModalVisible(true);
    };

    // 关闭编辑特别提醒弹窗
    const handleCancelRemindEdit = () => {
        setIsEditRemindModalVisible(false);
        setEditingRemindGuest(null);
        setEditingRemindText('');
    };

    // 提交特别提醒修改
    const handleSubmitRemindEdit = async () => {
        if (!editingRemindGuest) return;

        const remind = editingRemindText ?? '';
        setSavingRemind(true);
        try {
            const res = await updateGuestJourneyRemindApi({
                conferenceId,
                expertId: editingRemindGuest.id,
                remind,
            });

            if (res.status_code === 200) {
                message.success('特别提醒保存成功');
                getExpertList();
                handleCancelRemindEdit();
            } else {
                message.error(res.message || '保存失败');
            }
        } catch (error) {
            console.error('保存特别提醒失败:', error);
            message.error('保存失败');
        } finally {
            setSavingRemind(false);
        }
    };

    // 关闭住宿编辑弹窗
    const handleCancelHotelEdit = () => {
        setIsHotelEditModalVisible(false);
        setEditingHotelInfo(null);
    };

    // 打开用车变更弹窗
    const handleEditCar = (guestKey, type) => {
        // 获取嘉宾的原有用车数据
        const guest = data.find(g => g.key === guestKey);

        // 解析用车信息
        let carInfo = {};
        try {
            const carField = type === 'arrival' ? 'arrivalCar' : 'returnCar';
            if (guest?.guestJourneysEntity?.[carField]) {
                carInfo = JSON.parse(guest.guestJourneysEntity[carField]);
            }
        } catch (e) {
            console.error('解析用车信息失败:', e);
        }

        // 同时获取交通信息用于显示
        let transportInfo = {};
        try {
            const transportField = type === 'arrival' ? 'arrivalTransport' : 'returnTransport';
            if (guest?.guestJourneysEntity?.[transportField]) {
                transportInfo = JSON.parse(guest.guestJourneysEntity[transportField]);
            }
        } catch (e) {
            console.error('解析交通信息失败:', e);
        }

        // 设置正在编辑的用车信息
        setEditingCarInfo({
            guestKey,
            type,
            initialData: carInfo, // 用车信息
            journeyInfo: transportInfo, // 交通信息（用于显示）
        });
        setIsCarEditModalVisible(true);
    };

    // 关闭用车编辑弹窗
    const handleCancelCarEdit = () => {
        setIsCarEditModalVisible(false);
        setEditingCarInfo(null);
    };

    // 提交变更行程交通
    const handleSubmitJourneyEdit = values => {
        // 子组件已调用接口保存，这里只需刷新列表
        getExpertList();
        handleCancelJourneyEdit();
    };

    // 提交变更用车信息
    const handleSubmitCarEdit = values => {
        // 子组件已调用接口保存，这里只需刷新列表
        getExpertList();
        handleCancelCarEdit();
    };

    // 提交变更住宿信息
    const handleSubmitHotelEdit = values => {
        // 子组件已调用接口保存，这里需要刷新列表和酒店数据
        getExpertList();
        loadHotelList(); // 刷新酒店列表数据
        handleCancelHotelEdit();
    };

    // 处理出票状态变更
    const handleTicketStatusChange = async (guestKey, journeyType, transportIndex, newStatus) => {
        try {
            // 找到对应的嘉宾数据
            const guest = data.find(g => g.key === guestKey);
            if (!guest) {
                message.error('未找到嘉宾信息');
                return;
            }

            // 获取交通信息字段名
            const transportField = journeyType === 'arrival' ? 'arrivalTransport' : 'returnTransport';

            // 解析交通信息
            let transportInfo = {};
            try {
                if (guest?.guestJourneysEntity?.[transportField]) {
                    transportInfo = JSON.parse(guest.guestJourneysEntity[transportField]);
                }
            } catch (e) {
                console.error('解析交通信息失败:', e);
                message.error('解析交通信息失败');
                return;
            }

            // 更新指定交通项的出票状态
            if (transportInfo.transport_list && transportInfo.transport_list[transportIndex]) {
                const updatedTransportList = [...transportInfo.transport_list];
                updatedTransportList[transportIndex] = {
                    ...updatedTransportList[transportIndex],
                    ticket_ready: newStatus,
                };

                // 计算总出票状态
                // 0: 未出票（全部false）, 1: 全部出票（全部true）, 2: 部分出票（有true有false）
                const issuedCount = updatedTransportList.filter(t => t.ticket_ready === true).length;
                const totalCount = updatedTransportList.length;

                let totalTicketReady;
                if (issuedCount === 0) {
                    totalTicketReady = 0; // 全部未出票
                } else if (issuedCount === totalCount) {
                    totalTicketReady = 1; // 全部出票
                } else {
                    totalTicketReady = 2; // 部分出票
                }

                // 构建更新后的交通信息
                const updatedTransportInfo = {
                    ...transportInfo,
                    transport_list: updatedTransportList,
                    total_ticket_ready: totalTicketReady,
                };

                // 构建接口参数：{conferenceId, expertId, arrivalTransport/returnTransport}
                const saveParams = {
                    conferenceId: conferenceId,
                    expertId: guest.id,
                };

                // 根据journeyType添加对应的交通信息字段
                if (journeyType === 'arrival') {
                    saveParams.arrivalTransport = JSON.stringify(updatedTransportInfo);
                } else {
                    saveParams.returnTransport = JSON.stringify(updatedTransportInfo);
                }

                const res = await saveGuestJourney(saveParams);

                if (res.status_code === 200) {
                    message.success('出票状态更新成功');
                    // 刷新列表
                    getExpertList();

                    // 上报操作日志
                    try {
                        // 获取被变更的交通信息
                        const changedTransport = transportInfo.transport_list[transportIndex];
                        if (changedTransport) {
                            // 格式化交通信息
                            const transportInfoForLog = {
                                direction: journeyType === 'arrival' ? 'arrival' : 'return',
                                transports: [
                                    {
                                        transport_type: changedTransport.transport_type,
                                        transport_type_text: changedTransport.transport_type === 1 ? '飞机' : changedTransport.transport_type === 2 ? '火车' : '',
                                        transport_no: changedTransport.transport_no || '',
                                        seat_type: changedTransport.seat_type || '',
                                        departure_datetime: changedTransport.departure_datetime || '',
                                        arrival_datetime: changedTransport.arrival_datetime || '',
                                        departure_place: changedTransport.departure_place || '',
                                        arrival_place: changedTransport.arrival_place || '',
                                    },
                                ],
                            };

                            const logOptions = {
                                conferenceId,
                                expertId: guest.id,
                                expertName: guest.memUsername || '',
                                transportInfo: transportInfoForLog,
                                isSuccess: true,
                            };

                            // 根据出票状态调用不同的日志函数
                            if (newStatus) {
                                // 标记为已出票
                                await logIssueTicket(logOptions);
                            } else {
                                // 标记为未出票
                                await logCancelTicket(logOptions);
                            }
                        }
                    } catch (logError) {
                        console.error('操作日志上报失败:', logError);
                        // 日志上报失败不影响主流程
                    }
                } else {
                    message.error(res.message || '出票状态更新失败');
                }
            }
        } catch (error) {
            console.error('更新出票状态失败:', error);
            message.error('更新出票状态失败');
        }
    };

    // 从会议嘉宾列表等入口通过 location.state.guestName 携带嘉宾姓名检索（勿用 name，会议列表跳转 state.name 为会议名称）
    useEffect(() => {
        const guestName = location.state?.guestName;
        if (typeof guestName !== 'string') {
            return;
        }
        const trimmedName = guestName.trim();
        if (!trimmedName) {
            return;
        }
        const { filter: initialFilter } = conferenceSlice.getInitialState().guestJourney;
        dispatch(
            setFilter({
                ...initialFilter,
                name: trimmedName,
                page: 1,
            }),
        );
    }, [location.key, location.state, dispatch]);

    useEffect(() => {
        const newFilter = { ...filter };

        // 任务时间回显
        newFilter.taskBeginTime = filter.taskBeginTimeBegin && filter.taskBeginTimeEnd ? [dayjs(filter.taskBeginTimeBegin), dayjs(filter.taskBeginTimeEnd)] : undefined;
        newFilter.taskEndTime = filter.taskEndTimeBegin && filter.taskEndTimeEnd ? [dayjs(filter.taskEndTimeBegin), dayjs(filter.taskEndTimeEnd)] : undefined;

        // 来程/返程交通时间回显
        newFilter.arrivalTransportTime = filter.arrivalDatetimeBegin && filter.arrivalDatetimeEnd ? [dayjs(filter.arrivalDatetimeBegin), dayjs(filter.arrivalDatetimeEnd)] : undefined;
        newFilter.returnTransportTime = filter.returnDatetimeBegin && filter.returnDatetimeEnd ? [dayjs(filter.returnDatetimeBegin), dayjs(filter.returnDatetimeEnd)] : undefined;

        // 来程/返程用车时间回显
        newFilter.arrivalCarTime = filter.arrivalCarDatetimeBegin && filter.arrivalCarDatetimeEnd ? [dayjs(filter.arrivalCarDatetimeBegin), dayjs(filter.arrivalCarDatetimeEnd)] : undefined;
        newFilter.returnCarTime = filter.returnCarDatetimeBegin && filter.returnCarDatetimeEnd ? [dayjs(filter.returnCarDatetimeBegin), dayjs(filter.returnCarDatetimeEnd)] : undefined;

        // 入住/退房日期回显
        newFilter.checkinDate = filter.checkinDateBegin && filter.checkinDateEnd ? [dayjs(filter.checkinDateBegin), dayjs(filter.checkinDateEnd)] : undefined;
        newFilter.checkoutDate = filter.checkoutDateBegin && filter.checkoutDateEnd ? [dayjs(filter.checkoutDateBegin), dayjs(filter.checkoutDateEnd)] : undefined;

        if (filter.hotelId !== undefined) {
            newFilter.hotelName = filter.hotelId;
            // 如果有选中的酒店，初始化房型选项
            if (filter.hotelId && hotelOptions.length > 0) {
                const selectedHotel = hotelOptions.find(h => h.value === filter.hotelId);
                if (selectedHotel && selectedHotel.roomTypes) {
                    const roomTypes = selectedHotel.roomTypes.map(rt => ({
                        value: rt.id,
                        label: rt.roomType,
                    }));
                    setHotelRoomTypeOptions(roomTypes);
                }
                setSelectedHotelId(filter.hotelId);
            }
        }

        form.setFieldsValue(newFilter);
        getExpertList();
    }, [filter, hotelOptions]);

    useEffect(() => {
        if (!!conferenceId) {
            getConferenceInfo(conferenceId);
            getGuestJourneyTotal(conferenceId);
            getConferenceList();
            loadHotelList(); // 加载酒店列表
            loadVendorOptions(); // 加载服务商选项
        }

        let isHaveAuth = false;
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        isHaveAuth = roleList.some(item => item.name === '嘉宾行程管理');

        if (!isHaveAuth) {
            message.error('暂无权限');
            navigate(`/conf/conferences/${conferenceId}/guests`);
        }
    }, []);

    useEffect(() => {
        document.title = '嘉宾行程管理-医会议';
    }, []);

    // 打开分配服务商弹窗时加载服务商选项
    const loadVendorOptions = async () => {
        try {
            // 一次性获取所有已授权服务商及其完整权限列表
            const res = await listWithConferenceInfoApi({ conferenceId });
            const partners = res.status_code === 200 && res.data && res.data.conferencePartners ? res.data.conferencePartners : [];

            // 处理权限字段，统一为数组
            const processed = partners.map(p => ({
                id: p.id,
                partnerId: p.partnerId,
                partnerName: p.partnerName,
                permissions: Array.isArray(p.permissions) ? p.permissions : p.permissions ? p.permissions.split(',') : [],
            }));

            // 根据权限拆分三类服务商选项
            setTransportVendorOptions(processed.filter(p => p.permissions.includes('transport_mgmt')).map(p => ({ value: p.partnerId, label: p.partnerName })));
            setCarVendorOptions(processed.filter(p => p.permissions.includes('car_mgmt')).map(p => ({ value: p.partnerId, label: p.partnerName })));
            setHotelVendorOptions(processed.filter(p => p.permissions.includes('hotel_mgmt')).map(p => ({ value: p.partnerId, label: p.partnerName })));
        } catch (error) {
            console.error('获取服务商数据失败:', error);
            message.error('获取服务商数据失败');
        }
    };

    // 分配服务商 Modal
    const showBulkChangeVendor = async () => {
        if (selectedExpertsIds.length === 0) {
            message.warning('请先选择需要分配服务商的嘉宾');
            return;
        }

        // 设置选中的专家信息（用于Alert显示）
        const selectedExpertsInfo = data.filter(item => selectedExpertsIds.includes(item.id));
        setSelectedExperts(selectedExpertsInfo);

        await loadVendorOptions();
        setVisibleBulkChangeVendor(true);
    };

    // 确认批量更换服务商
    const confirmBulkVendor = () => {
        vendorForm.validateFields().then(async values => {
            Modal.confirm({
                title: '提示',
                content: `所选专家的授权服务商，将更新为您本次所选服务商，是否更新？`,
                onOk: async () => {
                    // 调用实际的API接口来更新服务商信息
                    try {
                        // 构造符合接口要求的数据结构
                        const requestData = {
                            conferenceId: conferenceId,
                            guestIds: selectedExpertsIds,
                            ...values,
                        };

                        // 调用API
                        const res = await savePartnersApi(requestData);
                        if (res.status_code === 200) {
                            message.success('更新成功');
                            setVisibleBulkChangeVendor(false);
                            setSelectedExperts([]);
                            setSelectedExpertsIds([]);
                            vendorForm.resetFields();
                            // 可以在这里刷新列表数据
                            getExpertList();
                        } else {
                            message.error('更新失败: ' + res.message);
                        }
                    } catch (error) {
                        message.error('更换服务商失败: ' + error.message);
                    }
                },
            });
        });
    };

    // 取消批量更换服务商
    const cancelBulkVendor = () => {
        setVisibleBulkChangeVendor(false);
        setSelectedExperts([]);
        setSelectedExpertsIds([]);
        vendorForm.resetFields();
    };

    // 更换行程需求
    const batchChangeJourneyNeeds = () => {
        if (selectedExpertsIds.length === 0) {
            message.warning('请先选择需要更换行程的嘉宾');
            return;
        }

        // 设置选中的专家信息（用于Alert显示）
        const selectedExpertsInfo = data.filter(item => selectedExpertsIds.includes(item.id));
        setSelectedExperts(selectedExpertsInfo);

        setVisibleConfirmJourney(true);
    };

    // 确认行程需求
    const confirmJourneyNeeds = () => {
        journeyForm.validateFields().then(values => {
            Modal.confirm({
                title: '提示',
                content: `所选专家的行程需求，将更新为您本次所选行程需求，是否更新？`,
                onOk: async () => {
                    // 调用实际的API接口来更新行程需求
                    try {
                        // 构造符合接口要求的数据结构
                        const requestData = {
                            arrivalCarNeed: values.arrivalCarNeed,
                            arrivalTransportNeed: values.arrivalTransportNeed,
                            conferenceId: conferenceId,
                            expertIds: selectedExpertsIds,
                            hotelNeed: values.hotelNeed,
                            returnCarNeed: values.returnCarNeed,
                            returnTransportNeed: values.returnTransportNeed,
                        };

                        // 调用API
                        const res = await batchUpdateNeedsApi(requestData);
                        if (res.status_code === 200) {
                            message.success('更新成功');
                            setVisibleConfirmJourney(false);
                            setSelectedExperts([]);
                            setSelectedExpertsIds([]);
                            // 可以在这里刷新列表数据
                            // getExpertList();
                        } else {
                            message.error('更新失败: ' + res.message);
                        }
                    } catch (error) {
                        message.error('更新失败: ' + error.message);
                    }
                },
            });
        });
    };

    // 取消行程需求
    const closeJourneyNeeds = () => {
        setVisibleConfirmJourney(false);
        setSelectedExperts([]);
        setSelectedExpertsIds([]);
        journeyForm.resetFields();
    };

    // 是否拥有嘉宾行程管理权限
    const canConfirmGuestJourney = useMemo(() => {
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        if (roleList && roleList.length) {
            return roleList.some(item => item.name === '嘉宾行程管理');
        }
        return false;
    }, []);

    // 导出行程单
    const exportGuestJourney = () => {
        window.location.href = `${config.API_PREFIX}/conference/boss/guestjourneys/exportGuestJourneysByConferenceId?conferenceId=${conferenceId}`;
    };

    return (
        <>
            <div className="page-container guest-journery-list-page">
                <PageHeader title="嘉宾行程" breadcrumb={[{ label: '医会议', path: CONFERENCE_WORKSPACE_PARENT_PATH }, { label: conference?.name || '项目信息', path: `${CONFERENCE_WORKSPACE_ROUTE_BASE}/${conferenceId}` }, '嘉宾行程']} {...filterToggleProps} />

                <FilterPanelSlot>
                    <GuestJourneyFilterPanel
                        filter={filter}
                        conferenceHallTreeData={conferenceHallTreeData}
                        dateOptions={dateOptions}
                        hotelOptions={hotelOptions}
                        hotelRoomTypeOptions={hotelRoomTypeOptions}
                        selectedHotelId={selectedHotelId}
                        collapsed={collapsed}
                        onFinish={onFinish}
                        onReset={onReset}
                        onToggleCollapsed={handleToggleCollapsed}
                        onHotelChange={handleHotelChange}
                    />
                </FilterPanelSlot>

                {!!guestTotalInfo && (
                    <div className="journey-total-info">
                        <Alert
                            title={
                                <>
                                    统计：已接受邀请{guestTotalInfo?.accepted}人；已录入行程{guestTotalInfo?.journeys}；总{guestTotalInfo?.rooms}间夜、住宿人数{guestTotalInfo?.journeyCheckins}人{getDateRoom(guestTotalInfo?.days)}
                                </>
                            }
                            type="info"
                        />
                    </div>
                )}

                <div ref={tableRegionRef} className="table-region-container guest-journey-table-region">
                    <div className="main-toolbar guest-journey-btns">
                        <div className="main-tools">
                            <Space>
                                <Button onClick={batchChangeJourneyNeeds}>更换行程需求</Button>
                                <Button onClick={showBulkChangeVendor}>分配服务商</Button>
                                <Button onClick={authorizeVendors}>授权服务商</Button>
                                <Button onClick={openHotelManagement}>酒店管理</Button>
                            </Space>
                        </div>
                        <div className="sub-tools">
                            <Space>
                                <Button onClick={settingGuestJourney}>嘉宾行程单设置</Button>

                                {canConfirmGuestJourney && (
                                    <Tooltip title="导出嘉宾行程">
                                        <Button
                                            type="default"
                                            icon={
                                                <span className="anticon">
                                                    <i className="bi bi-download"></i>
                                                </span>
                                            }
                                            onClick={exportGuestJourney}
                                        />
                                    </Tooltip>
                                )}

                                <Dropdown
                                    menu={{
                                        items: [
                                            { key: 0, label: <>{!filter?.orderMode ? <Link>按添加时间倒序（最新在前，默认）</Link> : '按添加时间倒序（最新在前，默认）'}</> },
                                            { key: 1, label: <>{filter?.orderMode === 1 ? <Typography.Link>按嘉宾姓名拼音字母排序（A→Z）</Typography.Link> : '按嘉宾姓名拼音字母排序（A→Z）'}</> },
                                            { key: 4, label: <>{filter?.orderMode === 4 ? <Typography.Link>按来程交通抵达时间正序（最早在前）</Typography.Link> : '按来程交通抵达时间正序（最早在前）'}</> },
                                            { key: 5, label: <>{filter?.orderMode === 5 ? <Typography.Link>按返程交通出发时间正序（最早在前）</Typography.Link> : '按返程交通出发时间正序（最早在前）'}</> },
                                        ],
                                        onClick: onClickSortDropdown,
                                    }}
                                >
                                    <Tooltip title="排序方式">
                                        <Button
                                            type="default"
                                            className={filter?.orderMode ? 'sort-button-active' : ''}
                                            icon={
                                                <span className="anticon">
                                                    <i className="bi bi-sort-down"></i>
                                                </span>
                                            }
                                        />
                                    </Tooltip>
                                </Dropdown>

                                <Tooltip title="刷新列表">
                                    <Button
                                        type="default"
                                        icon={
                                            <span className="anticon">
                                                <i className="bi bi-arrow-clockwise"></i>
                                            </span>
                                        }
                                        onClick={handleRefresh}
                                    />
                                </Tooltip>

                                <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                    <Button
                                        type="default"
                                        icon={
                                            <span className="anticon">
                                                <i className={`bi ${isFullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`}></i>
                                            </span>
                                        }
                                        onClick={handleToggleFullscreen}
                                    />
                                </Tooltip>
                            </Space>
                        </div>
                    </div>

                    <div className="list-table-wrap guest-journey-table-wrap">
                        <Table
                            className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                            rowKey="id"
                            rowSelection={{ type: 'checkbox', ...guestSelection }}
                            columns={columns}
                            dataSource={data}
                            loading={loading}
                            bordered
                            size="medium"
                            sticky
                            pagination={{
                                ...fullPagerConfig,
                                showSizeChanger: { placement: 'topRight' },
                                total,
                                showTotal: () => `共${total}条`,
                                current: filter.page,
                                pageSize: filter.pageSize,
                                onChange: onChangePage,
                            }}
                            scroll={{
                                x: 1700,
                                ...(tableScrollY != null ? { y: tableScrollY } : {}),
                            }}
                        />
                    </div>
                </div>
            </div>

            <Modal
                title="分配服务商"
                open={visibleBulkChangeVendor}
                onCancel={cancelBulkVendor}
                centered
                width={800}
                okText="确认服务商"
                footer={[
                    <Button key="confirm" type="primary" onClick={confirmBulkVendor}>
                        确认服务商
                    </Button>,
                ]}
            >
                <Alert title={<>已选专家：{selectedExperts.map(expert => expert.memUsername).join('、')}</>} style={{ marginBottom: 20 }} type="warning" showIcon={false} />
                <Form name="vendorForm" form={vendorForm}>
                    <Form.Item label="交通服务商" name="transportPartnerId">
                        <Select placeholder="请选择交通服务商" showSearch={{ optionFilterProp: 'children', filterOption: (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) }} options={transportVendorOptions} />
                    </Form.Item>

                    <Form.Item label="用车服务商" name="carPartnerId">
                        <Select placeholder="请选择用车服务商" showSearch={{ optionFilterProp: 'children', filterOption: (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) }} options={carVendorOptions} />
                    </Form.Item>

                    <Form.Item label="住宿服务商" name="hotelPartnerId">
                        <Select placeholder="请选择住宿服务商" showSearch={{ optionFilterProp: 'children', filterOption: (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) }} options={hotelVendorOptions} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="更换行程需求"
                open={visibleConfirmJourney}
                onCancel={closeJourneyNeeds}
                centered
                width={800}
                footer={[
                    <Button type="primary" onClick={confirmJourneyNeeds}>
                        确认行程需求
                    </Button>,
                ]}
            >
                <Alert title={<>已选专家：{selectedExperts.map(expert => expert.memUsername).join('、')}</>} style={{ marginBottom: 20 }} type="warning" showIcon={false} />
                <Form name="journeyForm" form={journeyForm} labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
                    <Form.Item label="来程交通需求" name="arrivalTransportNeed">
                        <Radio.Group>
                            <Radio value={1}>会务组安排</Radio>
                            <Radio value={2}>自费</Radio>
                            <Radio value={0}>不需要</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="返程交通需求" name="returnTransportNeed">
                        <Radio.Group>
                            <Radio value={1}>会务组安排</Radio>
                            <Radio value={2}>自费</Radio>
                            <Radio value={0}>不需要</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="来程用车需求" name="arrivalCarNeed">
                        <Radio.Group>
                            <Radio value={1}>会务组安排</Radio>
                            <Radio value={2}>自费</Radio>
                            <Radio value={0}>不需要</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="返程用车需求" name="returnCarNeed">
                        <Radio.Group>
                            <Radio value={1}>会务组安排</Radio>
                            <Radio value={2}>自费</Radio>
                            <Radio value={0}>不需要</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="住宿需求" name="hotelNeed">
                        <Radio.Group>
                            <Radio value={1}>会务组安排</Radio>
                            <Radio value={2}>自费</Radio>
                            <Radio value={0}>不需要</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal title="授权服务商" open={authServiceProviderOpen} onOk={handleAuthServiceOk} onCancel={handleAuthServiceCancel} width={800} centered className="add-service-provider" footer={null}>
                <Form labelCol={{ span: 4 }} wrapperCol={{ span: 20 }} form={authServiceForm} autoComplete="off">
                    <Form.Item label="授权截止日期" extra="授权服务商仅在授权截止日期前，可协同维护本项目专家行程信息">
                        {isEditingDeadline ? (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Form.Item name="deadline" noStyle>
                                    <DatePicker />
                                </Form.Item>
                                <Button type="link" onClick={saveAuthDeadline} style={{ marginLeft: 10 }}>
                                    保存
                                </Button>
                                <Button type="link" onClick={cancelEditAuthDeadline}>
                                    取消
                                </Button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span>{authDeadline ? dayjs(authDeadline).format('YYYY-MM-DD') : '未设置'}</span>
                                <Button
                                    type="link"
                                    onClick={() => {
                                        authServiceForm.setFieldsValue({ deadline: authDeadline });
                                        setIsEditingDeadline(true);
                                    }}
                                    style={{ marginLeft: 10 }}
                                >
                                    编辑
                                </Button>
                            </div>
                        )}
                    </Form.Item>
                    <Form.Item label="授权服务商">
                        <Button className="add-person-btn" type="link" onClick={addAuth} icon={<i className="bi bi-plus"></i>}>
                            新增授权
                        </Button>
                        <Table rowKey="id" columns={addAuthColumns} dataSource={authTableData} pagination={false} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal title={editingAuth ? '编辑授权' : '新增授权'} open={addAuthServiceOpen} onOk={handleAddAuthOk} onCancel={handleAddAuthCancel} width={600} centered className="add-service-provider">
                {editingAuth ? (
                    <Form labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} form={editAuthForm} autoComplete="off">
                        <Form.Item label="服务商名称">
                            <span>{editingAuth.partnerName}</span>
                        </Form.Item>
                        <Form.Item label="权限" name="permissions" rules={[{ required: true, message: '请选择权限' }]}>
                            <Checkbox.Group
                                options={[
                                    { label: '往返交通管理', value: 'transport_mgmt' },
                                    { label: '往返用车管理', value: 'car_mgmt' },
                                    { label: '酒店住宿管理', value: 'hotel_mgmt' },
                                ]}
                            />
                        </Form.Item>
                    </Form>
                ) : (
                    <Form labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} form={addAuthServiceForm} autoComplete="off">
                        <Form.Item name="partnerList" label="选择服务商" rules={[{ required: true, message: '请选择服务商' }]}>
                            <Select
                                placeholder="请输入服务商名称搜索"
                                mode="multiple"
                                options={vendorOptions}
                                showSearch={{
                                    onSearch: handleSearchVendor,
                                    filterOption: false,
                                }}
                                notFoundContent={vendorSearchValue ? '未找到相关服务商' : '请输入服务商名称搜索'}
                            />
                        </Form.Item>
                        <Form.Item label="权限" name="permissions" rules={[{ required: true, message: '请选择权限' }]}>
                            <Checkbox.Group
                                options={[
                                    { label: '往返交通管理', value: 'transport_mgmt' },
                                    { label: '往返用车管理', value: 'car_mgmt' },
                                    { label: '酒店住宿管理', value: 'hotel_mgmt' },
                                ]}
                            />
                        </Form.Item>
                    </Form>
                )}
            </Modal>

            <HotelManagementDrawer open={hotelDrawerVisible} onClose={closeHotelManagement} conferenceId={conferenceId} conference={conference} getConferenceInfo={getConferenceInfo} />

            <GuestJourneyImport importDataOpen={importDataOpen} setImportDataOpen={setImportDataOpen} getExpertList={getExpertList} />

            <GuestJourneySetting visibleGuestJourneySetDrawer={visibleGuestJourneySetDrawer} setVisibleGuestJourneySetDrawer={setVisibleGuestJourneySetDrawer} getConferenceInfo={getConferenceInfo} conference={conference} />

            {/* 变更行程交通弹窗（来程/返程通用） */}
            <JourneyEditModal
                type={editingJourneyInfo?.type || 'arrival'}
                open={isJourneyEditModalVisible}
                initialData={editingJourneyInfo?.initialData}
                conferenceId={conferenceId}
                expertId={editingJourneyInfo?.guestKey}
                guestInfo={
                    editingJourneyInfo?.guestKey
                        ? (() => {
                              const guest = data.find(g => g.key === editingJourneyInfo.guestKey);
                              const expertLevelLabel = expertLevels.find(a => a.level === guest?.expertLevel)?.label || '';

                              return {
                                  name: guest?.memUsername || '',
                                  gender: guest?.memGender,
                                  workExperiences: guest?.ssoMemberWorkExperiencesEntities || [],
                                  suggestedTime: getSuggestedTime(guest?.agendasGuestsList, editingJourneyInfo?.type),
                                  level: guest?.expertLevel,
                                  levelLabel: expertLevelLabel,
                              };
                          })()
                        : undefined
                }
                onCancel={handleCancelJourneyEdit}
                onSubmit={handleSubmitJourneyEdit}
            />

            {/* 变更住宿信息弹窗 */}
            <HotelEditModal
                open={isHotelEditModalVisible}
                initialData={editingHotelInfo?.initialData}
                hotelOptions={hotelOptions}
                conferenceId={conferenceId}
                expertId={editingHotelInfo?.guestKey}
                guestInfo={
                    editingHotelInfo?.guestKey
                        ? (() => {
                              const guest = data.find(g => g.key === editingHotelInfo.guestKey);
                              const expertLevelLabel = expertLevels.find(a => a.level === guest?.expertLevel)?.label || '';

                              // 获取建议入住日期（第一个已确认任务的日期）
                              const getSuggestedCheckInDate = () => {
                                  if (!guest.agendasGuestsList || guest.agendasGuestsList.length === 0) {
                                      return undefined;
                                  }

                                  // 找到第一个已确认的任务
                                  for (const task of guest.agendasGuestsList) {
                                      if (task.invitationAccepted === 1) {
                                          return dayjs(task.startTime).format('M月D日');
                                      }
                                  }
                                  return undefined;
                              };

                              // 获取建议退房日期（最后一个已确认任务的日期）
                              const getSuggestedCheckOutDate = checkInDate => {
                                  if (!guest.agendasGuestsList || guest.agendasGuestsList.length === 0) {
                                      return undefined;
                                  }

                                  let lastConfirmedDate;

                                  // 从后往前遍历，找到最后一个已确认的任务
                                  for (let i = guest.agendasGuestsList.length - 1; i >= 0; i--) {
                                      const task = guest.agendasGuestsList[i];
                                      if (task.invitationAccepted === 1) {
                                          lastConfirmedDate = dayjs(task.endTime).format('M月D日');
                                          break;
                                      }
                                  }

                                  if (!lastConfirmedDate) {
                                      return undefined;
                                  }

                                  // 如果入住和退房是同一天，退房日期取第二天
                                  if (checkInDate && lastConfirmedDate === checkInDate) {
                                      const dateMatch = lastConfirmedDate.match(/(\d+)月(\d+)日/);
                                      if (dateMatch) {
                                          const month = dateMatch[1];
                                          const day = parseInt(dateMatch[2]);
                                          return `${month}月${day + 1}日`;
                                      }
                                  }

                                  return lastConfirmedDate;
                              };

                              const suggestedCheckIn = getSuggestedCheckInDate();
                              const suggestedCheckOut = getSuggestedCheckOutDate(suggestedCheckIn);

                              return {
                                  name: guest?.memUsername || '',
                                  gender: guest?.memGender,
                                  workExperiences: guest?.ssoMemberWorkExperiencesEntities || [],
                                  suggestedCheckIn,
                                  suggestedCheckOut,
                                  level: guest?.expertLevel,
                                  levelLabel: expertLevelLabel,
                              };
                          })()
                        : undefined
                }
                onCancel={handleCancelHotelEdit}
                onSubmit={handleSubmitHotelEdit}
            />

            {/* 变更用车信息弹窗 */}
            <CarEditModal
                type={editingCarInfo?.type || 'arrival'}
                open={isCarEditModalVisible}
                initialData={editingCarInfo?.initialData}
                conferenceId={conferenceId}
                expertId={editingCarInfo?.guestKey}
                guestInfo={
                    editingCarInfo?.guestKey
                        ? (() => {
                              const guest = data.find(g => g.key === editingCarInfo.guestKey);
                              const expertLevelLabel = expertLevels.find(a => a.level === guest?.expertLevel)?.label || '';

                              return {
                                  name: guest?.memUsername || '',
                                  gender: guest?.memGender,
                                  workExperiences: guest?.ssoMemberWorkExperiencesEntities || [],
                                  suggestedTime: getSuggestedTime(guest?.agendasGuestsList, editingCarInfo?.type),
                                  level: guest?.expertLevel,
                                  levelLabel: expertLevelLabel,
                              };
                          })()
                        : undefined
                }
                journeyInfo={editingCarInfo?.journeyInfo}
                onCancel={handleCancelCarEdit}
                onSubmit={handleSubmitCarEdit}
            />

            {/* 交通变更记录弹窗（来程/返程通用） */}
            {viewingTransportHistory
                && (() => {
                    const guest = data.find(g => g.key === viewingTransportHistory.guestKey);
                    const expertLevelLabel = expertLevels.find(a => a.level === guest?.expertLevel)?.label || '';

                    return (
                        <TransportChangeHistoryModal
                            type={viewingTransportHistory.type}
                            open={isTransportHistoryModalVisible}
                            guestInfo={{
                                name: guest?.memUsername || '',
                                gender: guest?.memGender,
                                workExperiences: guest?.ssoMemberWorkExperiencesEntities || [],
                                level: guest?.expertLevel,
                                levelLabel: expertLevelLabel,
                            }}
                            changeRecords={transportChangeRecords}
                            loading={transportHistoryLoading}
                            onCancel={handleCancelTransportHistory}
                        />
                    );
                })()}

            {/* 编辑身份证号码弹窗 */}
            <IdCardEditModal
                open={isEditIdCardModalVisible}
                onCancel={handleCancelIdCardEdit}
                onOk={handleSubmitIdCardEdit}
                formData={{
                    idcardType: editingGuest?.idcardType || '',
                    idCard: editingGuest?.idCardStr || '',
                }}
            />

            {/* 编辑联系人弹窗 */}
            <Modal title="编辑联系人" open={isEditContactModalVisible} onOk={handleSubmitContactEdit} onCancel={handleCancelContactEdit} width={700} centered okText="保存" cancelText="取消">
                <Form form={contactForm} autoComplete="off">
                    <div className="contacts-box">
                        <Form.List name="contracts">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.length < 5 && (
                                        <Button type="link" size="small" onClick={() => add()} style={{ marginBottom: 16 }}>
                                            <span className="anticon">
                                                <i className="bi bi-plus-circle"></i>
                                            </span>
                                            <span>新增联系人</span>
                                        </Button>
                                    )}

                                    <div className="contact-list">
                                        {fields.map(({ key, name, ...restFields }, index) => (
                                            <div className="contact" key={key}>
                                                <Row gutter={20}>
                                                    <Col>
                                                        <Form.Item
                                                            name={[name, 'name']}
                                                            label="姓名"
                                                            rules={[
                                                                { required: true, message: '联系人姓名不能为空' },
                                                                { min: 2, message: '姓名不能少于2个字符' },
                                                            ]}
                                                        >
                                                            <Input placeholder="输入姓名" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col>
                                                        <Form.Item
                                                            name={[name, 'phone']}
                                                            label="手机号"
                                                            rules={[
                                                                { required: true, message: '手机号不能为空' },
                                                                { pattern: /^1\d{10}$/, message: '手机号格式不正确' },
                                                            ]}
                                                        >
                                                            <Input placeholder="输入手机号" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col>
                                                        <Button type="link" onClick={() => remove(name)}>
                                                            <span className="anticon">
                                                                <i className="bi bi-x-circle icon-pointer-projects"></i>
                                                            </span>
                                                            <span>移除</span>
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </Form.List>
                    </div>
                </Form>
            </Modal>

            {/* 编辑特别提醒弹窗 */}
            <Modal title="编辑特别提醒" open={isEditRemindModalVisible} onOk={handleSubmitRemindEdit} onCancel={handleCancelRemindEdit} confirmLoading={savingRemind} centered okText="保存" cancelText="取消">
                <Form.Item>
                    <div className="remind-edit-content">
                        <Input.TextArea value={editingRemindText} onChange={e => setEditingRemindText(e.target.value)} placeholder="请输入特别提醒内容，留空可清除提醒" rows={10} maxLength={500} showCount />
                        <div className="remind-edit-tip">{REMIND_PREVIEW_TIP}</div>
                    </div>
                </Form.Item>
            </Modal>
        </>
    );
};

export default GuestJourneyList;
